import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
// Relative rather than Helm's @/ paths, so the API's tests can render it.
import {
  dominantSource,
  languageName,
  offersTranslation,
  wantsTranslation,
  type BlockChoice,
  type TranslationContext,
  type TranslationResult,
} from '../../../lib/translation'
import { useTranslationSettings } from './TranslationProvider'

/** A callback ref for the block's element: it is translated once it is near the screen. */
export type ObserveBlock = (element: Element | null) => void | (() => void)

export interface TranslatedBlock {
  /** Some text in it is in another language than the person's: show the control. */
  offered: boolean
  /** Translation is on for this block: by its own toggle or the page's. */
  active: boolean
  /** Translations are on screen. */
  showing: boolean
  pending: boolean
  error: string | null
  /** "Chinese": what the shown translations were translated from. */
  from: string | null
  /** The text to show at this position: the translation, or the original. */
  text: (index: number) => string
  /** The block's own Translate / Show original. */
  toggle: () => void
  retry: () => void
}

const empty = () => () => undefined
const zero = () => 0

/**
 * One translatable block: a courier timeline, a note, a message. Give it the
 * block's texts and what they are; it says whether to offer "Translate" and
 * what to show, and gives a callback ref to put on the block's element.
 *
 * The page's "Translate page" turns every block on; a block's own toggle
 * overrides it until the page is switched again. A block is translated only
 * once it is near the screen, so a long page asks for what is read first,
 * and every block's request in that moment goes out together. The originals
 * show until translations arrive, and translations already known show at
 * once: nothing flickers. The record itself is never changed.
 */
export function useTranslated(
  texts: ReadonlyArray<string | null | undefined>,
  context: TranslationContext,
): [ObserveBlock, TranslatedBlock] {
  const settings = useTranslationSettings()
  const client = settings?.client ?? null
  const target = settings?.target ?? 'en'
  const pageOn = settings?.pageOn ?? false

  useSyncExternalStore(client?.subscribe ?? empty, client?.version ?? zero, zero)

  // The texts by value, so a block re-rendered with the same texts asks nothing new.
  const signature = JSON.stringify(texts.map((text) => text ?? ''))
  const sources = useMemo(() => JSON.parse(signature) as string[], [signature])
  const offerable = useMemo(
    () => sources.map((text) => Boolean(client) && offersTranslation(text, target)),
    [sources, client, target],
  )
  const offered = offerable.some(Boolean)

  const [choice, setChoice] = useState<BlockChoice | null>(null)
  const wanted = offered && wantsTranslation(choice, pageOn)

  const [visible, setVisible] = useState(false)
  const observe = useCallback((element: Element | null) => {
    if (!element) return
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true)
          observer.disconnect()
        }
      },
      // A screen's height ahead, so what is scrolled to next is ready.
      { rootMargin: '100% 0px' },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const wanting = useMemo(
    () => sources.filter((text, index) => offerable[index]),
    [sources, offerable],
  )
  const results: Array<TranslationResult | undefined> = sources.map((text, index) =>
    offerable[index] && client ? client.peek(text, target) : undefined,
  )
  const missing = client
    ? wanting.filter((text) => !client.peek(text, target) && !client.failure(text, target))
    : []
  const failure = client ? wanting.map((text) => client.failure(text, target)).find(Boolean) : null
  const pending = wanted && missing.length > 0 && !failure

  const missingKey = JSON.stringify(missing)
  useEffect(() => {
    const asked = JSON.parse(missingKey) as string[]
    if (!client || !wanted || !visible || asked.length === 0) return
    // Failures are kept on the client and shown on the block.
    client.translate(asked, target, context).catch(() => undefined)
  }, [client, wanted, visible, missingKey, target, context])

  const showing = wanted && results.some((result) => result?.translated)
  const toggle = useCallback(() => {
    setVisible(true)
    setChoice({ on: !wanted, page: pageOn })
  }, [wanted, pageOn])
  const retry = useCallback(() => client?.retry(wanting, target), [client, wanting, target])

  const block: TranslatedBlock = {
    offered,
    active: wanted,
    showing,
    pending,
    error: wanted && failure ? "Couldn't translate this." : null,
    from: showing ? languageName(dominantSource(results)) : null,
    text: (index) => {
      const result = results[index]
      return showing && result?.translated ? result.text : (sources[index] ?? '')
    },
    toggle,
    retry,
  }
  return [observe, block]
}
